/** Participant words for the choices the Decide page offers (content.md; docs/README.md §3 terms). */
import type { ContentCatalog } from "@bsim/contracts";
import { decisionPathLabel, type DecisionFamily, type Direction, type KpiId, type Objective } from "@bsim/domain";
import { LOWER_IS_BETTER, kpiLabel } from "../format/labels";
import { optionLabel, type SettingOption } from "./decisionValues";

type DeskSource = Pick<ContentCatalog, "decisions" | "desks">;

/** The families in the catalog's desk order (a catalog without desks falls back to the order its decisions are listed in). */
export const deskOrder = (catalog: DeskSource): readonly DecisionFamily[] =>
  catalog.desks === undefined || catalog.desks.length === 0
    ? [...new Set(catalog.decisions.map((d) => d.family))]
    : [...catalog.desks].sort((a, b) => a.order - b.order).map((d) => d.id as DecisionFamily);

/** A family as tabs and chips name it: its desk's label from the catalog; before the catalog has loaded (or without desks) the family's own name, capitalised. */
export const deskLabel = (catalog: Pick<ContentCatalog, "desks"> | undefined, family: DecisionFamily): string =>
  catalog?.desks?.find((d) => d.id === family)?.label ?? `${family.charAt(0).toUpperCase()}${family.slice(1)}`;
export type DeskLabeler = (family: DecisionFamily) => string;
export const deskLabeler = (catalog: Pick<ContentCatalog, "desks"> | undefined): DeskLabeler => (family) => deskLabel(catalog, family);

/** Aim chips (screens §P6 Rationale). */
export const OBJECTIVE_LABELS: Readonly<Record<Objective, string>> = {
  growNii: "Grow net interest income", growFranchise: "Grow franchise", protectCapital: "Protect capital", reduceIrr: "Reduce rate risk",
  improveLiquidity: "Improve liquidity", reduceConcentration: "Reduce concentration", manageCreditRisk: "Manage credit risk",
  improveEfficiency: "Improve efficiency", returnCapital: "Return capital", satisfyRegulator: "Satisfy the regulator",
  defendValuation: "Defend valuation", investInCapability: "Invest in capability",
};

export const DIRECTION_LABELS: Readonly<Record<Direction, string>> = { up: "Up", flat: "Flat", down: "Down" };

/** The direction as the sentence reads it ("You expect liquidity stress coverage to rise"): a measure where lower is better names its better side ("fall (better)", "rise (worse)"), so the chip says what the player is betting on. */
export const directionLabel = (kpi: KpiId, d: Direction): string => d === "flat" ? "hold" : LOWER_IS_BETTER.has(kpi) ? (d === "down" ? "fall (better)" : "rise (worse)") : d === "up" ? "rise" : "fall";
/** The three directions in the order the chips read: the better side of a lower-is-better measure first. */
export const directionsFor = (kpi: KpiId): readonly Direction[] => LOWER_IS_BETTER.has(kpi) ? ["down", "flat", "up"] : ["up", "flat", "down"];

/** "You expect" measures: every family's usual one plus the measures the call and the limits use. */
export const EXPECT_KPIS: readonly KpiId[] = [
  "nim", "ncoRate", "cet1Marked", "cet1Reported", "lcrLite", "efficiencyRatio", "loanGrowth", "costOfDeposits", "niiSensitivity", "rotce",
];

/** Measures an aim adds to the "You expect" list: a regulatory aim is judged on the regulator's capital ratio and the supervisory composite. */
const AIM_MEASURES: Readonly<Partial<Record<Objective, readonly KpiId[]>>> = {
  satisfyRegulator: ["cet1Reported", "camelsComposite"], protectCapital: ["cet1Reported", "camelsComposite"],
  improveLiquidity: ["lcrLite"], reduceIrr: ["niiSensitivity", "eve200"], improveEfficiency: ["efficiencyRatio"],
};

/**
 * The measure an aim is judged on when the player has not picked one: the family's usual measure, unless the aim has its own that the changed decisions also expect
 * (a liquidity aim on a treasury move reads liquidity). A measure the changes do not move is never suggested: a deposit-rate decision is not judged on CET1.
 */
export const suggestedMeasure = (aim: Objective | null, usual: KpiId, expected: readonly KpiId[] = []): KpiId => {
  const pool = expected.length === 0 ? [usual] : expected;
  const own = aim === null ? undefined : AIM_MEASURES[aim]?.find((k) => pool.includes(k));
  return own ?? (pool.includes(usual) ? usual : pool[0] ?? usual);
};

/** A measure as the rationale offers it: the plain name of the measures whose usual label is a code ("EVE at +200bp" reads as the question it answers). */
export const measureLabel = (kpi: KpiId, base: (k: KpiId) => string = kpiLabel): string => base(kpi);

/** The "You expect" measures to offer: `base`, then every measure any aim adds, each once. The list never depends on the aim picked, so it does not grow under the player; the aim only chooses which one is marked. */
export const expectMeasures = (base: readonly KpiId[]): readonly KpiId[] =>
  [...new Set([...base, ...Object.values(AIM_MEASURES).flat()])];

export const HORIZON_LABELS: Readonly<Record<number, string>> = { 1: "this year", 2: "in 2 years", 3: "in 3 years", 4: "in 4 years" };

/**
 * The caption over a composite decision's row. The buyback line reads from the option picked: "Buybacks: Off (no buybacks this year)" or
 * "Buybacks until reported CET1 reaches 9.25%"; every other row keeps its caption.
 */
export function subCaption(sub: string, value: SettingOption["value"] | undefined, options: readonly SettingOption[], fallback: string): string {
  if (sub !== "cet1TargetRate" || value === undefined) return fallback;
  return value === null ? "Buybacks: Off (no buybacks this year)" : `Buybacks until reported CET1 reaches ${optionLabel(options, value)}`;
}

/** A decision's plain name, said once: "Buyback target", never "Buyback target (target)" (a part that only repeats the decision's own last word is dropped). */
export const plainDecisionLabel = (path: string): string => decisionPathLabel(path).replace(/ \(target\)$/, "");
